import compact from 'lodash/compact';
import { VtexOrderForm } from './types';
import { CartData, CartSdkConfig } from '../../types';

export class VtexCart implements CartSdkConfig {
  private static readonly GIFT_CARD_REGEX = /^[A-Z]{4}-[A-Z]{4}-[A-Z]{4}-[A-Z]{4}$/;

  constructor() {
    const observer = new MutationObserver((mutations, obs) => {
      const button = document.getElementById('proceed-to-checkout');
      if (button) {
        button.addEventListener('click', () => this.onProceedToCheckoutClicked());

        obs.disconnect(); // Stop observing once done
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });
  }

  public async onLoadCart(): Promise<CartData> {
    if (window.location.hostname === 'localhost') {
      return {
        id: '',
        total: 1000,
        itemsTotal: 1000,
        discountCodes: [],
        items: [],
      };
    }

    const orderForm = await this.getOrderForm();
    const itemsTotal = orderForm.items.reduce((acc, item) => acc + (item.quantity * item.price), 0);

    return {
      id: orderForm.orderFormId,
      total: orderForm.value / 100,
      itemsTotal: itemsTotal / 100,
      discountCodes: compact([...orderForm.paymentData.giftCards.map((g) => g.redemptionCode), orderForm.marketingData?.coupon]),
      items: orderForm.items.map((item) => ({
        externalId: item.productId,
        price: item.price / 100,
        quantity: item.quantity,
        name: item.name,
      })),
    };
  }

  public async onRemoveDiscount(cart: CartData, code: string): Promise<{ success: boolean }> {
    if (window.location.hostname === 'localhost') {
      return { success: true };
    }

    if (!cart.id) {
      return { success: false };
    }

    try {
      if (VtexCart.GIFT_CARD_REGEX.test(code)) {
        return await this.removeGiftCard(cart.id);
      }
      return await this.removeCoupon(cart.id);
    } finally {
      if (window.location.pathname === '/checkout/') {
        window.location.reload();
      }
    }
  }

  public async onApplyDiscount(cart: CartData, code: string): Promise<{ success: boolean }> {
    if (window.location.hostname === 'localhost') {
      return { success: true };
    }

    if (!cart.id) {
      return { success: false };
    }

    try {
      if (VtexCart.GIFT_CARD_REGEX.test(code)) {
        return await this.applyGiftCard(cart.id, code);
      }
      return await this.applyCoupon(cart.id, code);
    } finally {
      if (window.location.pathname === '/checkout/') {
        window.location.reload();
      }
    }
  }

  private async onProceedToCheckoutClicked(): Promise<void> {
    const [
      { EventsStore },
      orderForm,
    ] = await Promise.all([
      import('@gamiphy/loyalty-station-desktop/store/events'),
      this.getOrderForm(),
    ]);

    await EventsStore.createEvent({
      type: 'checkoutStarted',
      cart: {
        id: orderForm.orderFormId,
        total: orderForm.value / 100,
      },
    });
  }

  private async applyGiftCard(orderFormId: string, code: string): Promise<{ success: boolean }> {
    await fetch(`/api/checkout/pub/orderForm/${orderFormId}/attachments/paymentData`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        giftCards: [{
          redemptionCode: code,
          inUse: true,
          isSpecialCard: false,
          provider: 'VtexGiftCard',
        }],
      }),
    });

    return { success: true };
  }

  private async removeGiftCard(orderFormId: string): Promise<{ success: boolean }> {
    await fetch(`/api/checkout/pub/orderForm/${orderFormId}/attachments/paymentData`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        giftCards: [],
      }),
    });

    return { success: true };
  }

  private async applyCoupon(orderFormId: string, code: string): Promise<{ success: boolean }> {
    await fetch(`/api/checkout/pub/orderForm/${orderFormId}/coupons`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text: code,
      }),
    });

    return { success: true };
  }

  private async removeCoupon(orderFormId: string): Promise<{ success: boolean }> {
    await fetch(`/api/checkout/pub/orderForm/${orderFormId}/coupons`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        text: '',
      }),
    });

    return { success: true };
  }

  private async getOrderForm(): Promise<VtexOrderForm> {
    const response = await fetch('/api/checkout/pub/orderForm?refreshOutdatedData=true');

    if (!response.ok) {
      throw new Error('Failed to retrieve vtex order form');
    }

    return (await response.json()) as VtexOrderForm;
  }
}
